import {
    Control,
    Controller,
    FieldError,
    FieldErrorsImpl,
    FieldValues,
    UseFormWatch,
} from 'react-hook-form';
import { ChangeEvent, ReactNode } from 'react';
import parsePhoneNumberFromString, {
    isPossiblePhoneNumber,
} from 'libphonenumber-js/max';
import { Merge } from 'type-fest';
import { useTranslation } from 'react-i18next';
import { Input } from '@src/components/ui';

export type FormPhoneProps = {
    name: string;
    keyName?: string;
    control?: Control<any>;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
    label?: string | ReactNode;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean | string;
    watch?: UseFormWatch<FieldValues>;
};

export function FormPhone({
    control,
    name,
    keyName,
    label,
    placeholder,
    disabled,
    error,
    required,
}: FormPhoneProps) {
    const { t } = useTranslation();

    const getErrorMessage = () => {
        if (!error) return '';

        if (typeof error.message === 'string') {
            return error.message;
        }

        if (error.message) {
            return t(String(error.message), { name: t(`forms.${name}`) });
        }

        return '';
    };

    const invalidMessage = () =>
        t('errors.formErrors.isNotValid', { name: t(`forms.${name}`) });

    const validatePhone = (value: string) => {
        if (!value) return true;
        const normalized = value.replace(/[^\d+]/g, '');
        const phone = parsePhoneNumberFromString(normalized);
        if (!phone) {
            return invalidMessage();
        }
        const isValid = phone.isValid() && isPossiblePhoneNumber(normalized);
        if (!isValid) {
            return invalidMessage();
        }
        const type = phone.getType();
        if (type !== 'MOBILE') {
            return invalidMessage();
        }

        return true;
    };

    const handleChange = (
        e: ChangeEvent<HTMLInputElement>,
        onChange: (value: string) => void
    ) => {
        let value = e.target.value.trim();

        if (!value) {
            onChange('');
            return;
        }

        value = value.replace(/[^+\d]/g, '');
        value = value.replace(/^(\++)/, '+');
        value = value.replace(/(?!^)\+/g, '');
        if (value[0] !== '+') {
            value = '+' + value.replace(/\+/g, '');
        }

        onChange(value);
    };

    return (
        <div className="flex flex-col gap-1">
            {label && (
                <label>
                    {typeof label === 'string'
                        ? t(label)
                        : (label as ReactNode)}
                </label>
            )}

            <Controller
                control={control}
                name={name ?? keyName}
                rules={{
                    required: required
                        ? {
                              value: true,
                              message:
                                  typeof required === 'string'
                                      ? required
                                      : t('errors.formErrors.required', {
                                            name: t(`forms.${name}`),
                                        }),
                          }
                        : false,
                    validate: validatePhone,
                }}
                render={({ field }) => (
                    <>
                        <Input
                            {...field}
                            size="large"
                            placeholder={t(placeholder ?? '+90XXXXXXXXXX')}
                            disabled={disabled}
                            maxLength={16}
                            status={error ? 'error' : ''}
                            onChange={(e) => handleChange(e, field.onChange)}
                        />
                        {error && (
                            <span className="text-red-500 text-xs mt-1">
                                {getErrorMessage()}
                            </span>
                        )}
                    </>
                )}
            />
        </div>
    );
}
